﻿import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Check,
    ArrowLeft,
    Users,
    Search,
    Home,
    Package,
    ChevronDown,
    ChevronRight,
    Tag,
    Euro
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAlert } from '../../components/AlertProvider';
import { useGlobalErrorHandler } from '../../hooks/useGlobalErrorHandler';
import { useAuth } from '../../contexts/AuthContext';
import {
    calculatePercentagePrice,
    calculateUGPrice,
    detectTier,
    UGCalculationResult,
    PricingTier
} from '../../utils/pricingCalculations';
import { buildTierGroupKey, extractUnitName } from '../../utils/globalTierCalculations';

interface Consumable {
    id: number;
    title: string;
    description: string | null;
    unit: string;
    unit_id: number;
    marque?: string | null;
    gamme?: string | null;
    base_price: number;
    custom_price?: number;
    currency: string;
    batch_size: number | null;
    min_order_quantity: number | null;
    volume_ml?: number;
    is_active?: boolean | null;
    tiers?: PricingTier[];
    discount_percentage?: number | null;
    free_units?: number | null;
}

interface Client {
    id: number;
    company_name: string;
    email: string;
    payment_mode?: string | null;
    payment_terms?: string | null;
    invoice_email?: string | null;
    address?: string | null;
    city?: string | null;
    postal_code?: string | null;
    country?: string | null;
    central_purchasing_id?: number | null;
    use_central_purchasing_bpu?: boolean | null;
}

interface SalesPoint {
    id: number;
    name: string;
    address: string | null;
    city: string | null;
    postal_code: string | null;
}

interface ClientAddress {
    id: number;
    sales_point_id: number | null;
    label: string | null;
    kind: 'billing' | 'shipping';
    address: string;
    city: string;
    postal_code: string;
    country: string;
    is_default: boolean;
}

interface PricingItem {
    id: number;
    consumable_id: number;
    custom_price: number;
    discount_percentage: number | null;
    tiers?: PricingTier[];
}

interface ClientPricing {
    id: number;
    client_id: number;
    is_active: boolean;
    status: string;
    pricing_items?: PricingItem[];
}

interface CartItem {
    consumable: Consumable;
    quantity: number;
    unitPrice: number;
    batchSize: number;
    pricingCalculation?: UGCalculationResult;
    unit?: string;
    volume_ml?: number;
}

interface GroupTotals {
    totalLots: number;
    totalUnits: number;
    batchSize: number;
    unitName?: string;
}

interface OrderTotals {
    totalHt: number;
    totalBeforeDiscount: number;
    totalGain: number;
    creditNoteValue: number;
    finalHt: number;
    taxAmount: number;
    totalTtc: number;
}

interface CategoryGroup {
    key: string;
    marque: string;
    gamme: string;
    unitName: string;
    products: Consumable[];
    tiers: PricingTier[];
    totalLots: number;
    totalUnits: number;
    batchSize: number;
}

interface UnitTiersResponse {
    tiers: PricingTier[];
    tiers_by_unit_id: Record<number, PricingTier[]>;
    tiers_by_unit_key?: Record<string, PricingTier[]>;
}

const AdminNewOrder: React.FC = () => {
    const TVA_RATE: number = 5.5;
    const navigate = useNavigate();
    const { user } = useAuth();
    const { showSuccess, showError: _showError } = useAlert();
    const { handleError } = useGlobalErrorHandler();
    const [cart, setCart] = useState<CartItem[]>([]);
    const [consumables, setConsumables] = useState<Consumable[]>([]);
    const [clients, setClients] = useState<Client[]>([]);
    const [salesPoints, setSalesPoints] = useState<SalesPoint[]>([]);
    const [addresses, setAddresses] = useState<ClientAddress[]>([]);
    const [selectedClient, setSelectedClient] = useState<number | null>(null);
    const [selectedClientData, setSelectedClientData] = useState<Client | null>(null);
    const [selectedSalesPoint, setSelectedSalesPoint] = useState<number | null>(null);
    const [selectedShippingAddress, setSelectedShippingAddress] = useState<number | null>(null);
    const [pricing, setPricing] = useState<ClientPricing[]>([]);
    const [unitTiersById, setUnitTiersById] = useState<Record<number, PricingTier[]>>({});
    const [unitTiersByKey, setUnitTiersByKey] = useState<Record<string, PricingTier[]>>({});
    const [notes] = useState('');
    const [clientReference, setClientReference] = useState<string>('');
    const [invoiceEmail, setInvoiceEmail] = useState<string>('');
    const [paymentMode, setPaymentMode] = useState<string>('Virement');
    const [paymentTerms, setPaymentTerms] = useState<string>('30 jours FDM');
    const [creditNoteAmount, setCreditNoteAmount] = useState<number>(0);
    const [creditNotePercentage, setCreditNotePercentage] = useState<number>(0);
    const [creditNoteType, setCreditNoteType] = useState<'amount' | 'percentage'>('amount');
    const [creditNoteReason, setCreditNoteReason] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [loadingClients, setLoadingClients] = useState(true);
    const [loadingConsumables, setLoadingConsumables] = useState(true);
    const [productSearch, setProductSearch] = useState('');
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [clientSearch, setClientSearch] = useState('');
    const [showClientDropdown, setShowClientDropdown] = useState(false);

    const fetchClients = useCallback(async (searchTerm?: string) => {
        try {
            setLoadingClients(true);
            const term = searchTerm?.trim();
            const params: Record<string, unknown> = { limit: 200 };
            if (term) params.search = term;
            if (user?.role === 'apporteur') params.mine_only = true;
            params.has_sales_points = true;
            const response = await api.get('/clients', { params });
            const list: Client[] = Array.isArray(response.data) ? response.data : (response.data?.items ?? []);
            setClients(list);
        } catch (error) {
            handleError(error, 'Erreur lors du chargement des clients');
            setClients([]);
        } finally {
            setLoadingClients(false);
        }
    }, [handleError, user?.role]);

    const fetchConsumables = useCallback(async () => {
        try {
            setLoadingConsumables(true);
            const response = await api.get('/consumables', { params: { is_active: true } });
            setConsumables(response.data);
        } catch (error) {
            handleError(error, 'Erreur lors du chargement du catalogue');
        } finally {
            setLoadingConsumables(false);
        }
    }, [handleError]);

    const fetchSalesPoints = useCallback(async (clientId: number) => {
        try {
            const response = await api.get(`/clients/sales-points?client_id=${clientId}`);
            const raw = response.data;
            const list: SalesPoint[] = Array.isArray(raw) ? raw : (Array.isArray(raw?.items) ? raw.items : []);
            setSalesPoints(list);
            if (list.length > 0) {
                setSelectedSalesPoint((prev) => {
                    // If no previous selection or previous selection not in list, select first
                    if (!prev || !list.some((sp) => sp.id === prev)) {
                        return list[0].id;
                    }
                    return prev;
                });
            } else {
                setSelectedSalesPoint(null);
            }
        } catch (error) {
            handleError(error, 'Erreur lors du chargement des points de vente');
            setSalesPoints([]);
            setSelectedSalesPoint(null);
        }
    }, [handleError]);

    const fetchAddresses = useCallback(async (clientId: number) => {
        try {
            const response = await api.get(`/clients/${clientId}/addresses`);
            setAddresses(response.data);
        } catch (error) {
            handleError(error, 'Erreur lors du chargement des adresses');
            setAddresses([]);
        }
    }, [handleError]);

    const fetchPricing = useCallback(async (clientId: number) => {
        try {
            const res = await api.get(`/client-pricing?client_id=${clientId}`);
            const list = Array.isArray(res.data) ? (res.data as ClientPricing[]) : (res.data?.items || []);
            console.log('[AdminNewOrder] Fetched pricing list:', list);
            const detailedList = await Promise.all(
                list.map(async (bpu: ClientPricing) => {
                    try {
                        const detailRes = await api.get(`/client-pricing/${bpu.id}`);
                        console.log(`[AdminNewOrder] Detailed BPU ${bpu.id}:`, detailRes.data);
                        return detailRes.data;
                    } catch (err) {
                        console.error(`[AdminNewOrder] Error fetching BPU ${bpu.id}:`, err);
                        return bpu;
                    }
                })
            );
            console.log('[AdminNewOrder] Final pricing data:', detailedList);
            setPricing(detailedList);

            // Fetch gamme-level tiers for the active BPU
            const activeBpu = detailedList.find((bpu: ClientPricing) => bpu.status === 'validated' && bpu.is_active)
                || detailedList.find((bpu: ClientPricing) => bpu.is_active && bpu.status !== 'archived')
                || detailedList[0];
            if (activeBpu?.id) {
                await fetchUnitTiers(activeBpu.id);
            }
        } catch (error) {
            console.error('[AdminNewOrder] Error in fetchPricing:', error);
            setPricing([]);
        }
    }, []);

    const fetchUnitTiers = useCallback(async (bpuId: number) => {
        try {
            const res = await api.get<UnitTiersResponse>(`/client-pricing/${bpuId}/all-unit-tiers`);
            console.log('[AdminNewOrder] Fetched gamme-level tiers:', res.data);
            console.log('[AdminNewOrder] tiers_by_unit_id keys:', Object.keys(res.data.tiers_by_unit_id || {}));
            console.log('[AdminNewOrder] tiers count:', res.data.tiers?.length || 0);
            // Log each tier for debugging
            Object.entries(res.data.tiers_by_unit_id || {}).forEach(([unitId, tiers]: [string, any]) => {
                console.log(`[AdminNewOrder] Unit ID "${unitId}":`, tiers);
            });
            // tiers_by_unit_id is keyed by unit_id (number)
            setUnitTiersById(res.data.tiers_by_unit_id || {});
            setUnitTiersByKey(res.data.tiers_by_unit_key || {});
        } catch (error) {
            console.error('[AdminNewOrder] Error fetching unit tiers:', error);
            setUnitTiersById({});
            setUnitTiersByKey({});
        }
    }, []);

    useEffect(() => {
        void fetchConsumables();
    }, [fetchConsumables]);

    useEffect(() => {
        void fetchClients();
    }, [fetchClients]);

    useEffect(() => {
        if (!showClientDropdown) return;
        const delay = setTimeout(() => {
            const term = clientSearch.trim();
            void fetchClients(term ? term : undefined);
        }, 300);
        return () => clearTimeout(delay);
    }, [clientSearch, showClientDropdown, fetchClients]);

    useEffect(() => {
        if (selectedClient) {
            void fetchSalesPoints(selectedClient);
            void fetchAddresses(selectedClient);
            void fetchPricing(selectedClient);
            setCart([]);
        } else {
            setSalesPoints([]);
            setAddresses([]);
            setPricing([]);
            setSelectedShippingAddress(null);
            setSelectedSalesPoint(null);
            setSelectedClientData(null);
            setCart([]);
        }
    }, [selectedClient, fetchSalesPoints, fetchAddresses, fetchPricing]);

    useEffect(() => {
        if (!selectedClient) return;
        if (selectedSalesPoint) return;
        if (salesPoints.length === 0) return;
        setSelectedSalesPoint(salesPoints[0].id);
    }, [selectedClient, salesPoints, selectedSalesPoint]);

    const createSalesPointAddress = useCallback((salesPoint: SalesPoint): ClientAddress => ({
        id: -salesPoint.id,
        sales_point_id: salesPoint.id,
        label: `${salesPoint.name} (Point de vente)`,
        kind: 'shipping',
        address: salesPoint.address || '',
        city: salesPoint.city || '',
        postal_code: salesPoint.postal_code || '',
        country: 'France',
        is_default: false,
    }), []);

    const filteredAddresses = useCallback((kind: 'billing' | 'shipping'): ClientAddress[] => {
        if (kind === 'billing') {
            const regularAddresses = addresses.filter(a => a.kind === kind);
            if (regularAddresses.length === 0 && selectedClientData) {
                if (selectedClientData.address && selectedClientData.city && selectedClientData.postal_code) {
                    return [{
                        id: -999,
                        sales_point_id: null,
                        label: `${selectedClientData.company_name} (Profil)`,
                        kind,
                        address: selectedClientData.address,
                        city: selectedClientData.city,
                        postal_code: selectedClientData.postal_code,
                        country: selectedClientData.country || 'France',
                        is_default: true,
                    }];
                }
            }
            return regularAddresses;
        }

        if (selectedSalesPoint) {
            const selectedSp = salesPoints.find(sp => sp.id === selectedSalesPoint);
            if (selectedSp) {
                return [createSalesPointAddress(selectedSp)];
            }
        }

        return [];
    }, [addresses, selectedClientData, selectedSalesPoint, salesPoints, createSalesPointAddress]);

    useEffect(() => {
        if (selectedSalesPoint) {
            const selectedSp = salesPoints.find(sp => sp.id === selectedSalesPoint);
            if (selectedSp) {
                setSelectedShippingAddress(-selectedSp.id);
            }
        }
    }, [selectedSalesPoint, salesPoints]);

    useEffect(() => {
        if (!selectedClient) {
            setSelectedClientData(null);
            return;
        }
        const c = clients.find((x) => x.id === selectedClient) ?? null;
        setSelectedClientData(c);
    }, [selectedClient, clients]);

    useEffect(() => {
        if (!selectedClientData) {
            setInvoiceEmail('');
            setPaymentMode('Virement');
            setPaymentTerms('30 jours FDM');
            return;
        }

        setInvoiceEmail(selectedClientData.invoice_email || selectedClientData.email || '');
        setPaymentMode(selectedClientData.payment_mode || 'Virement');
        setPaymentTerms(selectedClientData.payment_terms || '30 jours FDM');
    }, [selectedClientData]);

    const resolvePricingClientId = useCallback((client: Client | null, fallbackId: number | null): number | null => {
        if (!client) return fallbackId;
        if (client.central_purchasing_id && client.use_central_purchasing_bpu) {
            return client.central_purchasing_id;
        }
        return client.id ?? fallbackId;
    }, []);

    const getActiveBpu = useCallback((): ClientPricing | undefined => {
        if (!pricing || pricing.length === 0) return undefined;
        const validatedBpu = pricing.find(bpu => bpu.status === 'validated' && bpu.is_active);
        if (validatedBpu) return validatedBpu;
        const activeBpu = pricing.find(bpu => bpu.is_active && bpu.status !== 'archived');
        if (activeBpu) return activeBpu;
        return pricing.find(bpu => (bpu.pricing_items?.length || 0) > 0);
    }, [pricing]);

    const availableConsumables: Consumable[] = useMemo(() => {
        const activeBpu = getActiveBpu();
        if (!activeBpu?.pricing_items || activeBpu.pricing_items.length === 0) return [];
        const allowedIds = new Set(activeBpu.pricing_items.map((item) => item.consumable_id));
        return consumables.filter(
            (item: Consumable) => allowedIds.has(item.id) && item.is_active === true
        );
    }, [consumables, getActiveBpu]);

    const filteredConsumables = availableConsumables.filter((item: Consumable) =>
        item.title.toLowerCase().includes(productSearch.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(productSearch.toLowerCase()))
    );

    const buildUnitVolumeKey = useCallback((unitId: number, batchSize: number | null | undefined): string => {
        const normalizedBatchSize: number = batchSize ?? 1;
        return `${unitId}:${normalizedBatchSize}`;
    }, []);

    const buildGroupedTotalsByUnitId = useCallback((items: CartItem[]): Map<number, GroupTotals> => {
        const map: Map<number, GroupTotals> = new Map();
        items.forEach((item: CartItem) => {
            const unitId = item.consumable.unit_id;

            if (!unitId) return;
            const batchSize: number = item.consumable.batch_size ?? 1;
            const current: GroupTotals | undefined = map.get(unitId);
            const nextLots: number = (current?.totalLots ?? 0) + item.quantity;
            const nextUnits: number = (current?.totalUnits ?? 0) + (item.quantity * batchSize);
            const unitName = extractUnitName(item.consumable.title, item.consumable.unit) || item.consumable.unit;
            map.set(unitId, {
                totalLots: nextLots,
                totalUnits: nextUnits,
                batchSize: current?.batchSize ?? batchSize,
                unitName: current?.unitName ?? unitName
            });
        });
        return map;
    }, []);

    const buildGroupedTotalsByUnitKey = useCallback((items: CartItem[]): Map<string, GroupTotals> => {
        const map: Map<string, GroupTotals> = new Map();
        items.forEach((item: CartItem) => {
            const unitId: number = item.consumable.unit_id;
            if (!unitId) return;
            const batchSize: number = item.consumable.batch_size ?? 1;
            const unitKey: string = buildUnitVolumeKey(unitId, batchSize);
            const current: GroupTotals | undefined = map.get(unitKey);
            const nextLots: number = (current?.totalLots ?? 0) + item.quantity;
            const nextUnits: number = (current?.totalUnits ?? 0) + (item.quantity * batchSize);
            const unitName = extractUnitName(item.consumable.title, item.consumable.unit) || item.consumable.unit;
            map.set(unitKey, {
                totalLots: nextLots,
                totalUnits: nextUnits,
                batchSize: current?.batchSize ?? batchSize,
                unitName: current?.unitName ?? unitName
            });
        });
        return map;
    }, [buildUnitVolumeKey]);

    const groupedTotalsByUnitId: Map<number, GroupTotals> = useMemo((): Map<number, GroupTotals> => {
        return buildGroupedTotalsByUnitId(cart);
    }, [cart, buildGroupedTotalsByUnitId]);

    const groupedTotalsByUnitKey: Map<string, GroupTotals> = useMemo((): Map<string, GroupTotals> => {
        return buildGroupedTotalsByUnitKey(cart);
    }, [cart, buildGroupedTotalsByUnitKey]);

    const categoryGroups: CategoryGroup[] = useMemo((): CategoryGroup[] => {
        const groups: Map<string, CategoryGroup> = new Map();

        filteredConsumables.forEach((product: Consumable) => {
            const unitName = extractUnitName(product.title, product.unit) || product.unit;
            const key = `${product.unit_id}|${buildTierGroupKey(product.marque, product.gamme, unitName)}`;
            const unitKey: string = buildUnitVolumeKey(product.unit_id, product.batch_size);

            const marque = product.marque || 'Sans marque';
            const gamme = product.gamme || 'Sans gamme';
            const tiers: PricingTier[] = unitTiersByKey[unitKey] || unitTiersById[product.unit_id] || [];
            const groupTotals: GroupTotals | undefined = groupedTotalsByUnitId.get(product.unit_id) || groupedTotalsByUnitKey.get(unitKey);
            const totalLots: number = groupTotals?.totalLots ?? 0;
            const totalUnits: number = groupTotals?.totalUnits ?? 0;
            const batchSize: number = groupTotals?.batchSize ?? product.batch_size ?? 1;

            if (!groups.has(key)) {
                groups.set(key, {
                    key,
                    marque,
                    gamme,
                    unitName,
                    products: [product],
                    tiers,
                    totalLots,
                    totalUnits,
                    batchSize
                });
                return;
            }

            const group = groups.get(key)!;
            group.products.push(product);
            if (group.tiers.length === 0 && tiers.length > 0) {
                group.tiers = tiers;
            }
            group.totalLots = totalLots;
            group.totalUnits = totalUnits;
            group.batchSize = batchSize;
        });

        return Array.from(groups.values()).sort((a: CategoryGroup, b: CategoryGroup) => {
            if (a.marque !== b.marque) return a.marque.localeCompare(b.marque);
            if (a.gamme !== b.gamme) return a.gamme.localeCompare(b.gamme);
            return a.unitName.localeCompare(b.unitName);
        });
    }, [buildUnitVolumeKey, filteredConsumables, groupedTotalsByUnitId, groupedTotalsByUnitKey, unitTiersById, unitTiersByKey]);

    const toggleCategory = (key: string) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const validatePanachage = useCallback((): { valid: boolean; errors: string[] } => {
        const errors: string[] = [];
        // Group by unit_id only so that products with different batch sizes but same unit type are merged
        const groupedByUnitId = buildGroupedTotalsByUnitId(cart);

        for (const [, group] of groupedByUnitId.entries()) {
            const { totalUnits, batchSize, unitName } = group;
            if (batchSize <= 1) continue;
            if (totalUnits % batchSize !== 0) {
                const remaining = totalUnits % batchSize;
                const needed = batchSize - remaining;
                errors.push(
                    `${unitName}: ${totalUnits} unités = ${(totalUnits / batchSize).toFixed(2)} lots. ` +
                    `Ajoutez ${needed} unité${needed > 1 ? 's' : ''} ou retirez ${remaining} pour obtenir un multiple de ${batchSize}.`
                );
            }
        }

        return { valid: errors.length === 0, errors };
    }, [cart, buildGroupedTotalsByUnitId]);

    const validationErrors: string[] = useMemo(() => validatePanachage().errors, [validatePanachage]);

    const canSubmit = (): boolean => {
        return Boolean(selectedClient && selectedShippingAddress && cart.length > 0 && validationErrors.length === 0);
    };

    const calculateClientPrice = useCallback((consumable: Consumable): number => {
        const activeBpu = getActiveBpu();
        if (!activeBpu?.pricing_items) return consumable.base_price;
        const item = activeBpu.pricing_items.find(i => i.consumable_id === consumable.id);
        if (!item) return consumable.base_price;

        // Use discount percentage if available to ensure real-time price evolution
        if (item.discount_percentage !== null && item.discount_percentage !== undefined) {
            return consumable.base_price * (1 - item.discount_percentage / 100);
        }

        // Fallback to custom_price if no discount is set (should not happen with new logic but for safety)
        return item.custom_price || consumable.base_price;
    }, [getActiveBpu]);

    const calculateItemPricingWithGroupedLots = useCallback(
        (consumable: Consumable, itemLots: number, groupedLots: number): UGCalculationResult | undefined => {
            const unitKey: string = buildUnitVolumeKey(consumable.unit_id, consumable.batch_size);
            let tiers: PricingTier[] = unitTiersByKey[unitKey] || unitTiersById[consumable.unit_id] || [];

            // if (tiers.length === 0) {
            //     // Try base unit name (first word, pluralized) - "boite 8 gel" -> "boites"
            //     const baseUnit = unitKey.split(' ')[0];
            //     const pluralUnit = baseUnit.endsWith('s') ? baseUnit : baseUnit + 's';

            //     tiers = unitTiersById[pluralUnit] || unitTiersById[baseUnit] || [];
            // }
            if (!tiers || tiers.length === 0) return undefined;

            const batchSize: number = consumable.batch_size ?? 1;
            const basePrice: number = calculateClientPrice(consumable);
            const orderedUnits: number = itemLots * batchSize;
            const tier: PricingTier | null = detectTier(groupedLots, tiers);

            if (!tier) {
                return {
                    ordered_units: orderedUnits,
                    free_units: 0,
                    total_received: orderedUnits,
                    unit_price: basePrice,
                    total_cost: orderedUnits * basePrice,
                    display_text: `${itemLots} lot${itemLots > 1 ? 's' : ''}`,
                    tier_triggered: false
                };
            }

            let result: UGCalculationResult;
            if (tier.free_units !== null && tier.free_units !== undefined && tier.free_units > 0) {
                result = calculateUGPrice(orderedUnits, basePrice, tier.free_units, itemLots);
            } else if (tier.discount_percentage !== null && tier.discount_percentage !== undefined) {
                result = calculatePercentagePrice(orderedUnits, basePrice, tier.discount_percentage, itemLots);
            } else {
                result = {
                    ordered_units: orderedUnits,
                    free_units: 0,
                    total_received: orderedUnits,
                    unit_price: basePrice,
                    total_cost: orderedUnits * basePrice,
                    display_text: `${itemLots} lot${itemLots > 1 ? 's' : ''}`,
                    tier_triggered: false
                };
            }

            result.tier_number = tier.tier_number;
            return result;
        },
        [buildUnitVolumeKey, calculateClientPrice, unitTiersById, unitTiersByKey]
    );

    const applyGroupedTierPricing = useCallback((items: CartItem[]): CartItem[] => {
        const groupedTotalsByKey: Map<string, GroupTotals> = buildGroupedTotalsByUnitKey(items);
        const groupedTotalsById: Map<number, GroupTotals> = buildGroupedTotalsByUnitId(items);
        return items.map((item: CartItem) => {
            const unitKey: string = buildUnitVolumeKey(item.consumable.unit_id, item.consumable.batch_size);
            const groupedLots: number = groupedTotalsById.get(item.consumable.unit_id)?.totalLots
                ?? groupedTotalsByKey.get(unitKey)?.totalLots
                ?? item.quantity;
            const unitPrice: number = calculateClientPrice(item.consumable);
            const pricingCalculation = calculateItemPricingWithGroupedLots(item.consumable, item.quantity, groupedLots);
            return { ...item, unitPrice, pricingCalculation };
        });
    }, [buildGroupedTotalsByUnitId, buildGroupedTotalsByUnitKey, buildUnitVolumeKey, calculateClientPrice, calculateItemPricingWithGroupedLots]);

    const normalizeQuantity = (quantity: number, minOrderQty: number): number => {
        if (quantity <= 0) return 0;
        return Math.max(Math.floor(quantity), minOrderQty);
    };

    const buildCartItem = useCallback(
        (consumable: Consumable, quantity: number): CartItem => {
            const unitPrice = calculateClientPrice(consumable);
            const batchSize = consumable.batch_size ?? 1;
            return {
                consumable,
                quantity,
                unitPrice,
                batchSize,
                unit: consumable.unit,
                volume_ml: consumable.volume_ml
            };
        },
        [calculateClientPrice]
    );

    const setProductQuantity = useCallback(
        (consumable: Consumable, quantity: number): void => {
            const minOrderQty = consumable.min_order_quantity ?? 1;
            const normalizedQuantity = normalizeQuantity(quantity, minOrderQty);

            setCart((prevCart: CartItem[]) => {
                if (normalizedQuantity === 0) {
                    return prevCart.filter((item: CartItem) => item.consumable.id !== consumable.id);
                }

                const existingItem = prevCart.find(
                    (item: CartItem) => item.consumable.id === consumable.id
                );
                const nextCart = existingItem
                    ? prevCart.map((item: CartItem) =>
                        item.consumable.id === consumable.id
                            ? { ...item, quantity: normalizedQuantity }
                            : item
                    )
                    : [...prevCart, buildCartItem(consumable, normalizedQuantity)];

                return applyGroupedTierPricing(nextCart);
            });
        },
        [applyGroupedTierPricing, buildCartItem]
    );

    useEffect(() => {
        if (!selectedClient) return;
        const pricingClientId = resolvePricingClientId(selectedClientData, selectedClient);
        if (!pricingClientId) return;
        void fetchPricing(pricingClientId);
    }, [selectedClient, selectedClientData, resolvePricingClientId, fetchPricing]);

    useEffect(() => {
        if (!pricing || pricing.length === 0) return;
        setCart(prevCart => applyGroupedTierPricing(prevCart));
    }, [pricing, applyGroupedTierPricing]);

    const orderTotals = useMemo((): OrderTotals => {
        const totalBeforeDiscount = cart.reduce((sum: number, item: CartItem) => {
            return sum + (item.quantity * (item.unitPrice * item.batchSize));
        }, 0);
        const totalHt = cart.reduce((sum: number, item: CartItem) => {
            if (item.pricingCalculation) return sum + item.pricingCalculation.total_cost;
            return sum + (item.quantity * (item.unitPrice * item.batchSize));
        }, 0);
        const totalGain = Math.max(0, totalBeforeDiscount - totalHt);
        const creditNoteValue = creditNoteType === 'percentage'
            ? (totalHt * (creditNotePercentage / 100))
            : (creditNoteAmount || 0);
        const taxableAmount = Math.max(0, totalHt - creditNoteValue);
        const taxAmount = (taxableAmount * TVA_RATE) / 100;
        const totalTtc = taxableAmount + taxAmount;

        return {
            totalHt,
            totalBeforeDiscount,
            totalGain,
            creditNoteValue,
            finalHt: taxableAmount,
            taxAmount,
            totalTtc
        };
    }, [cart, TVA_RATE, creditNoteAmount, creditNotePercentage, creditNoteType]);

    const handleSubmit = async () => {
        if (!selectedClient) return _showError('Veuillez sélectionner un client');
        if (cart.length === 0) return _showError('Veuillez ajouter au moins un article');

        // Validation du panachage
        if (validationErrors.length > 0) {
            _showError('Quantités invalides. Veuillez corriger les erreurs de panachage.');
            return;
        }

        setLoading(true);
        try {
            const c = selectedClientData;
            const orderData = {
                client_id: selectedClient,
                sales_point_id: selectedSalesPoint,
                billing_address_id: filteredAddresses('billing').find(a => a.is_default)?.id || filteredAddresses('billing')[0]?.id,
                shipping_address_id: selectedShippingAddress,
                notes: notes,
                client_reference: clientReference.trim() || null,
                invoice_email: invoiceEmail || c?.email || '',
                payment_mode: paymentMode,
                payment_terms: paymentTerms,
                credit_note_amount: creditNoteType === 'amount' ? (creditNoteAmount || 0) : 0,
                credit_note_percentage: creditNoteType === 'percentage' ? (creditNotePercentage || 0) : 0,
                credit_note_type: creditNoteType,
                credit_note_reason: (creditNoteType === 'amount' ? creditNoteAmount : creditNotePercentage) > 0 ? (creditNoteReason || '') : '',
                items: cart.map(item => ({
                    consumable_id: item.consumable.id,
                    quantity: item.quantity,
                    unit_price: item.unitPrice
                }))
            };
            await api.post('/purchase-orders', orderData);
            showSuccess(`Commande créée avec succès!`);
            navigate('/achat/orders');
        } catch (error) {
            handleError(error, 'Erreur lors de la création de la commande');
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-950 p-3 sm:p-6">
            <div className="max-w-7xl mx-auto mb-4 sm:mb-6">
                <button
                    onClick={() => navigate('/achat/orders')}
                    className="flex items-center gap-2 text-sm sm:text-base text-slate-600 dark:text-slate-400 hover:text-orange-600 dark:hover:text-orange-400 mb-3 sm:mb-4"
                >
                    <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
                    Retour aux commandes
                </button>
                <h1 className="text-xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100">Créer une commande</h1>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">Créez une nouvelle commande pour un client</p>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                {/* Left column: Client, Sales Point, Address */}
                <div className="lg:col-span-1 relative z-20">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 sticky top-6">
                        {/* Client Section */}
                        <div className="p-4 border-b border-slate-100 dark:border-slate-800">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="p-1.5 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                                    <Users className="h-4 w-4 text-orange-600" />
                                </div>
                                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Client</span>
                                {selectedClient && <Check className="h-4 w-4 text-emerald-500 ml-auto" />}
                            </div>
                            {loadingClients ? (
                                <div className="space-y-2">
                                    <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse" />
                                </div>
                            ) : (
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setShowClientDropdown(!showClientDropdown)}
                                        className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm text-left focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all flex items-center justify-between"
                                    >
                                        <span className={selectedClient ? '' : 'text-slate-400'}>
                                            {selectedClient
                                                ? clients.find(c => c.id === selectedClient)?.company_name
                                                : 'Sélectionner un client...'}
                                        </span>
                                        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${showClientDropdown ? 'rotate-180' : ''}`} />
                                    </button>
                                    {showClientDropdown && (
                                        <>
                                            <div
                                                className="fixed inset-0 z-30"
                                                onClick={() => {
                                                    setShowClientDropdown(false);
                                                    setClientSearch('');
                                                }}
                                            />
                                            <div className="absolute z-40 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl shadow-lg overflow-hidden">
                                                <div className="p-2 border-b border-slate-200 dark:border-slate-700">
                                                    <div className="relative">
                                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                                        <input
                                                            type="text"
                                                            value={clientSearch}
                                                            onChange={(e) => setClientSearch(e.target.value)}
                                                            placeholder="Rechercher..."
                                                            className="w-full pl-9 pr-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                                                            onClick={(e) => e.stopPropagation()}
                                                            autoFocus
                                                        />
                                                    </div>
                                                </div>
                                                <div className="max-h-60 overflow-y-auto">
                                                    {clients
                                                        .sort((a, b) => a.company_name.localeCompare(b.company_name))
                                                        .map((client) => (
                                                            <div
                                                                key={client.id}
                                                                onClick={() => {
                                                                    setSelectedClient(client.id);
                                                                    setShowClientDropdown(false);
                                                                    setClientSearch('');
                                                                }}
                                                                className={`px-3 py-2.5 cursor-pointer text-sm transition-colors ${selectedClient === client.id
                                                                    ? 'bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300'
                                                                    : 'hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-900 dark:text-slate-100'
                                                                    }`}
                                                            >
                                                                <div className="font-medium">{client.company_name}</div>
                                                                <div className="text-xs text-slate-500">{client.email}</div>
                                                            </div>
                                                        ))}
                                                    {!loadingClients && clients.length === 0 && (
                                                        <div className="px-3 py-3 text-sm text-slate-500">
                                                            Aucun client trouvé.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Sales Point Section */}
                        {selectedClient && salesPoints.length > 0 && (
                            <div className="p-4 border-b border-slate-100 dark:border-slate-800">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                                        <Home className="h-4 w-4 text-blue-600" />
                                    </div>
                                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Point de vente</span>
                                </div>
                                <select
                                    value={selectedSalesPoint || ''}
                                    onChange={(e) => setSelectedSalesPoint(Number(e.target.value) || null)}
                                    className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all"
                                >
                                    {salesPoints.map(sp => (
                                        <option key={sp.id} value={sp.id}>{sp.name} {sp.city && `· ${sp.city}`}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Delivery Address Section */}
                        {selectedClient && (
                            <div className="p-4 border-b border-slate-100 dark:border-slate-800">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="p-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                                        <Home className="h-4 w-4 text-emerald-600" />
                                    </div>
                                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Livraison</span>
                                </div>
                                <div className="space-y-2">
                                    {filteredAddresses('shipping').map(addr => (
                                        <label
                                            key={addr.id}
                                            className={`block p-3 rounded-xl border-2 cursor-pointer transition-all ${selectedShippingAddress === addr.id
                                                ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20'
                                                : 'border-transparent bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700'
                                                }`}
                                        >
                                            <div className="flex items-start gap-2">
                                                <input
                                                    type="radio"
                                                    checked={selectedShippingAddress === addr.id}
                                                    onChange={() => setSelectedShippingAddress(addr.id)}
                                                    className="mt-1 accent-emerald-500"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{addr.label || 'Livraison'}</span>
                                                        {addr.is_default && (
                                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500 text-white font-medium">Défaut</span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">{addr.address}, {addr.postal_code} {addr.city}</p>
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Billing Section */}
                        {selectedClient && (
                            <div className="p-4 bg-slate-50/50 dark:bg-slate-800/30">
                                <div className="p-4 border-b border-slate-100 dark:border-slate-800">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/30"><Tag className="h-4 w-4 text-blue-600" /></div>
                                        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Référence client</span>
                                        <span className="text-xs text-slate-400">(optionnel)</span>
                                    </div>
                                    <input type="text" value={clientReference} onChange={(e) => setClientReference(e.target.value)} placeholder="Votre référence..." className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-800" />
                                </div>
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="p-1.5 rounded-lg bg-violet-100 dark:bg-violet-900/30">
                                        <Package className="h-4 w-4 text-violet-600" />
                                    </div>

                                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Facturation</span>
                                </div>

                                <div className="space-y-3">

                                    <input
                                        type="email"
                                        value={invoiceEmail}
                                        onChange={(e) => setInvoiceEmail(e.target.value)}
                                        placeholder="Email de facturation"
                                        className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 focus:bg-white transition-all"
                                    />
                                    <div className="grid grid-cols-2 gap-2">
                                        <select
                                            value={paymentMode}
                                            onChange={(e) => setPaymentMode(e.target.value)}
                                            className="px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all"
                                        >
                                            <option value="Virement">Virement</option>
                                            <option value="Prélèvement">Prélèvement</option>
                                        </select>
                                        <select
                                            value={paymentTerms}
                                            onChange={(e) => setPaymentTerms(e.target.value)}
                                            className="px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all"
                                        >
                                            <option value="">Comptant</option>
                                            <option value="30 jours FDM">30j FDM</option>
                                            <option value="45 jours FDM">45j FDM</option>
                                            <option value="60 jours FDM">60j FDM</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right column: Products catalog */}
                <div className="lg:col-span-2 space-y-4 relative z-10">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                        {/* Header */}
                        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <div className="p-2 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 shadow-lg shadow-orange-500/20">
                                        <Package className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Catalogue</h2>
                                        {/* <p className="text-xs text-slate-500">{categoryGroups.length} catégories disponibles</p> */}
                                    </div>
                                </div>
                            </div>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <input
                                    type="text"
                                    value={productSearch}
                                    onChange={(e) => setProductSearch(e.target.value)}
                                    placeholder="Rechercher un produit..."
                                    className="w-full h-10 pl-10 pr-4 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 focus:bg-white transition-all"
                                />
                            </div>
                        </div>

                        {/* Categories */}
                        <div className="max-h-[480px] overflow-y-auto">
                            {loadingConsumables ? (
                                <div className="p-5 space-y-4">
                                    {[1, 2, 3].map((i) => (
                                        <div key={i} className="space-y-3">
                                            <div className="h-12 bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse" />
                                            <div className="pl-8 space-y-2">
                                                <div className="h-16 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse" />
                                                <div className="h-16 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : categoryGroups.map((group: CategoryGroup) => {
                                const isExpanded = expandedCategories.has(group.key);
                                const sortedTiers: PricingTier[] = [...group.tiers].sort(
                                    (a: PricingTier, b: PricingTier) => a.min_quantity - b.min_quantity
                                );
                                const activeTier: PricingTier | null = detectTier(group.totalLots, sortedTiers);
                                const nextTier: PricingTier | undefined = sortedTiers.find(
                                    (tier: PricingTier) => tier.min_quantity > group.totalLots
                                );
                                const remainingLots: number | null = nextTier
                                    ? Math.max(0, nextTier.min_quantity - group.totalLots)
                                    : null;
                                const categoryTotal = group.products.reduce((sum, item) => {
                                    const cartItem = cart.find(c => c.consumable.id === item.id);
                                    if (!cartItem) return sum;
                                    return sum + (cartItem.pricingCalculation?.total_cost ?? 0);
                                }, 0);

                                return (
                                    <div key={group.key} className={`border-b last:border-b-0 ${isExpanded ? 'border-slate-200 dark:border-slate-700' : 'border-slate-200 dark:border-slate-700'}`}>
                                        {/* Category Header */}
                                        <button
                                            type="button"
                                            onClick={() => toggleCategory(group.key)}
                                            className={`w-full px-4 py-3.5 flex items-center gap-3 transition-all ${isExpanded
                                                ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-100 text-slate-900 border-l-4 border-orange-400 shadow-sm ring-1 ring-orange-200/60 dark:from-slate-800 dark:via-slate-800 dark:to-slate-900 dark:text-white dark:ring-0'
                                                : 'bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-750 border-l-4 border-l-orange-300'
                                                }`}
                                        >
                                            <div className={`p-1.5 rounded-lg transition-colors ${isExpanded ? 'bg-white/80 border border-orange-200 shadow-sm dark:bg-orange-500/20 dark:border-orange-400/40' : 'bg-orange-100 dark:bg-orange-900/30'}`}>
                                                {isExpanded ? <ChevronDown className="h-4 w-4 text-orange-600 dark:text-orange-200" /> : <ChevronRight className="h-4 w-4 text-orange-600 dark:text-orange-400" />}
                                            </div>
                                            <div className="flex-1 text-left min-w-0">
                                                <p className={`text-sm font-bold truncate ${isExpanded ? 'text-slate-900 dark:text-white' : 'text-slate-800 dark:text-slate-100'}`}>
                                                    {group.marque} · {group.gamme}{group.unitName ? ` | ${group.unitName}` : ''}
                                                </p>
                                            </div>

                                            {/* Tier badges */}
                                            <div className="flex items-center gap-1.5">
                                                {sortedTiers.map((tier: PricingTier) => {
                                                    const isActive = group.totalLots >= tier.min_quantity;
                                                    return (
                                                        <span
                                                            key={tier.tier_number}
                                                            title={`Palier ${tier.tier_number}: À partir de ${tier.min_quantity} lots`}
                                                            className={`text-[10px] px-2.5 py-1 rounded-full font-bold transition-all cursor-help ${isActive
                                                                ? 'bg-emerald-500 text-white shadow-sm'
                                                                : isExpanded
                                                                    ? 'bg-orange-200 text-orange-700 dark:bg-slate-600 dark:text-slate-300'
                                                                    : 'bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400'
                                                                }`}
                                                        >
                                                            {tier.discount_percentage ? `-${tier.discount_percentage}%` : `+${tier.free_units}U`}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </button>

                                        {/* Category Tier Status */}
                                        {isExpanded && (remainingLots && remainingLots > 0 && nextTier) && (
                                            <div className="mx-5 mb-2 p-2.5 rounded-lg bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-950/30 dark:to-red-950/30 border border-orange-200 dark:border-orange-900/50">
                                                <div className="flex items-center justify-center text-xs">
                                                    <span className="text-orange-600 dark:text-orange-400 font-semibold">
                                                        +{remainingLots} lot{remainingLots > 1 ? 's' : ''} pour débloquer {nextTier.discount_percentage}% de remise
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        {isExpanded && activeTier && !(remainingLots && remainingLots > 0 && nextTier) && (
                                            <div className="mx-5 mb-2 p-2.5 rounded-lg bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-950/30 dark:to-emerald-900/30 border border-emerald-200 dark:border-emerald-900/50">
                                                <div className="flex items-center justify-center text-xs">
                                                    <span className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400 font-semibold">
                                                        <Check className="h-3.5 w-3.5" />
                                                        Dernier palier débloqué
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Products Table Header - Desktop only */}
                                        {isExpanded && (
                                            <div className="hidden sm:block px-5 py-2 bg-slate-100/50 dark:bg-slate-800/50 border-y border-slate-100 dark:border-slate-700/50 animate-in fade-in slide-in-from-top-2 duration-200">
                                                <div className="flex items-center gap-4 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                                                    <div className="flex-1">Produit</div>
                                                    <div className="w-24 text-center">Palier</div>
                                                    <div className="w-20 text-right">Prix unit.</div>
                                                    <div className="w-24 text-right">Prix / lot</div>
                                                    <div className="w-16 text-center">Qté</div>
                                                    <div className="w-28 text-right">Total</div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Products */}
                                        {isExpanded && (
                                            <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                                {group.products.map((item: Consumable) => {
                                                    const cartItem: CartItem | undefined = cart.find(
                                                        (entry: CartItem) => entry.consumable.id === item.id
                                                    );
                                                    const quantity: number = cartItem?.quantity ?? 0;
                                                    const basePrice: number = calculateClientPrice(item);
                                                    const batchSize: number = item.batch_size ?? 1;
                                                    const minOrderQty: number = item.min_order_quantity ?? 1;
                                                    const baseUnitPrice: number = basePrice;
                                                    const baseLotPrice: number = basePrice * batchSize;
                                                    const unitKey: string = buildUnitVolumeKey(item.unit_id, item.batch_size);
                                                    const groupedLotsForItem: number = groupedTotalsByUnitId.get(item.unit_id)?.totalLots
                                                        ?? groupedTotalsByUnitKey.get(unitKey)?.totalLots
                                                        ?? quantity;
                                                    const activeTierForItem: PricingTier | null = detectTier(groupedLotsForItem, sortedTiers);
                                                    const tierNumber: number | null = activeTierForItem?.tier_number ?? null;
                                                    const discountPercent: number = activeTierForItem?.discount_percentage ?? 0;
                                                    // Calculate prices based on current active tier, not stale pricing
                                                    const unitPrice: number = quantity > 0 && activeTierForItem?.discount_percentage
                                                        ? basePrice * (1 - activeTierForItem.discount_percentage / 100)
                                                        : basePrice;
                                                    const lotPrice: number = unitPrice * batchSize;
                                                    const lineTotal: number = quantity * lotPrice;
                                                    const hasDiscount: boolean = quantity > 0 && discountPercent > 0;

                                                    return (
                                                        <div key={item.id}>
                                                            {/* Mobile product card */}
                                                            <div className={`sm:hidden px-3 py-2.5 border-b border-slate-50 dark:border-slate-800/50 last:border-b-0 transition-all duration-200 ${quantity > 0 ? 'bg-orange-50/40 dark:bg-orange-900/5' : ''}`}>
                                                                <div className="flex items-center gap-2 mb-1.5">
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className={`text-xs font-medium truncate ${quantity > 0 ? 'text-slate-900 dark:text-slate-100' : 'text-slate-700 dark:text-slate-300'}`}>{item.title}</p>
                                                                        <p className="text-[10px] text-slate-400">
                                                                            Lot de {batchSize} {item.unit}
                                                                            {minOrderQty > 1 && <span className="ml-1 text-orange-500 font-medium">· Min: {minOrderQty}</span>}
                                                                        </p>
                                                                    </div>
                                                                    {hasDiscount && tierNumber && (
                                                                        <span className="shrink-0 px-1.5 py-0.5 rounded bg-emerald-500 text-white text-[9px] font-bold">P{tierNumber} -{discountPercent}%</span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <div className="flex-1 text-[10px] text-slate-500">
                                                                        {hasDiscount && <span className="line-through mr-1">{baseLotPrice.toFixed(2)}€</span>}
                                                                        <span className="font-semibold text-slate-700 dark:text-slate-300">{lotPrice.toFixed(2)}€/lot</span>
                                                                    </div>
                                                                    <div className="flex items-center">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setProductQuantity(item, quantity - minOrderQty < minOrderQty ? 0 : quantity - minOrderQty)}
                                                                            className={`w-6 h-7 flex items-center justify-center rounded-l-lg border border-r-0 text-xs font-bold transition-colors ${quantity > 0 ? 'border-orange-300 bg-orange-50 text-orange-600 active:bg-orange-100' : 'border-slate-200 bg-slate-100 text-slate-400'}`}
                                                                        >−</button>
                                                                        <input
                                                                            type="number"
                                                                            min={0}
                                                                            step={minOrderQty}
                                                                            value={quantity === 0 ? '' : quantity}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value === '' ? 0 : Number(e.target.value);
                                                                                setProductQuantity(item, Number.isNaN(val) ? 0 : val);
                                                                            }}
                                                                            onBlur={(e) => { if (e.target.value === '') setProductQuantity(item, 0); }}
                                                                            placeholder="0"
                                                                            className={`w-10 h-7 border-y px-0 text-center text-xs font-semibold focus:outline-none ${quantity > 0 ? 'border-orange-300 bg-white text-orange-600' : 'border-slate-200 bg-slate-50 text-slate-900'}`}
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setProductQuantity(item, quantity === 0 ? minOrderQty : quantity + minOrderQty)}
                                                                            className={`w-6 h-7 flex items-center justify-center rounded-r-lg border border-l-0 text-xs font-bold transition-colors ${quantity > 0 ? 'border-orange-300 bg-orange-50 text-orange-600 active:bg-orange-100' : 'border-slate-200 bg-slate-100 text-slate-400 active:bg-slate-200'}`}
                                                                        >+</button>
                                                                    </div>
                                                                    <div className="w-16 text-right">
                                                                        {quantity > 0 ? (
                                                                            <p className="text-xs font-bold text-slate-900 dark:text-slate-100">{lineTotal.toFixed(2)}€</p>
                                                                        ) : (
                                                                            <p className="text-xs text-slate-300">—</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Desktop product row */}
                                                            <div
                                                                className={`hidden sm:flex px-5 py-3 items-center gap-4 border-b border-slate-50 dark:border-slate-800/50 last:border-b-0 transition-all duration-200 ${quantity > 0 ? 'bg-orange-50/40 dark:bg-orange-900/5 shadow-sm' : 'hover:bg-slate-100/80 dark:hover:bg-slate-700/30 hover:shadow-sm'}`}
                                                            >
                                                                {/* Product Info */}
                                                                <div className="flex-1 min-w-0">
                                                                    <p className={`text-sm font-medium truncate ${quantity > 0 ? 'text-slate-900 dark:text-slate-100' : 'text-slate-700 dark:text-slate-300'}`}>{item.title}</p>
                                                                    <p className="text-[11px] text-slate-400">
                                                                        Lot de {batchSize} {item.unit}
                                                                        {minOrderQty > 1 && <span className="ml-2 text-orange-500 font-medium">· Min: {minOrderQty}</span>}
                                                                    </p>
                                                                </div>

                                                                {/* Tier Badge */}
                                                                <div className="w-24 flex justify-center">
                                                                    {hasDiscount && tierNumber ? (
                                                                        <div className="inline-flex flex-col items-center gap-0.5">
                                                                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-[10px] font-bold shadow-sm">
                                                                                Palier {tierNumber}
                                                                            </span>
                                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-[10px] font-semibold">
                                                                                -{discountPercent}%
                                                                            </span>
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
                                                                    )}
                                                                </div>

                                                                {/* Unit Price */}
                                                                <div className="w-20 text-right">
                                                                    {hasDiscount && (
                                                                        <p className="text-[10px] text-slate-400 line-through">{baseUnitPrice.toFixed(2)} €</p>
                                                                    )}
                                                                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                                        {unitPrice.toFixed(2)} €
                                                                    </p>
                                                                </div>

                                                                {/* Price per lot */}
                                                                <div className="w-24 text-right">
                                                                    {hasDiscount && (
                                                                        <p className="text-[10px] text-slate-400 line-through">{baseLotPrice.toFixed(2)} €</p>
                                                                    )}
                                                                    <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                                                                        {lotPrice.toFixed(2)} €
                                                                    </p>
                                                                </div>

                                                                {/* Quantity Input */}
                                                                <div className="w-20">
                                                                    <div className="flex items-center">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setProductQuantity(item, quantity - minOrderQty < minOrderQty ? 0 : quantity - minOrderQty)}
                                                                            className={`w-7 h-8 flex items-center justify-center rounded-l-lg border border-r-0 text-sm font-bold transition-colors ${quantity > 0 ? 'border-orange-300 bg-orange-50 dark:bg-orange-950/30 text-orange-600 active:bg-orange-100' : 'border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 text-slate-400'}`}
                                                                        >−</button>
                                                                        <input
                                                                            type="number"
                                                                            min={0}
                                                                            step={minOrderQty}
                                                                            value={quantity === 0 ? '' : quantity}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value === '' ? 0 : Number(e.target.value);
                                                                                setProductQuantity(item, Number.isNaN(val) ? 0 : val);
                                                                            }}
                                                                            onBlur={(e) => { if (e.target.value === '') setProductQuantity(item, 0); }}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'ArrowUp') {
                                                                                    e.preventDefault();
                                                                                    const newQty = quantity === 0 ? minOrderQty : quantity + minOrderQty;
                                                                                    setProductQuantity(item, newQty);
                                                                                } else if (e.key === 'ArrowDown') {
                                                                                    e.preventDefault();
                                                                                    const newQty = quantity - minOrderQty;
                                                                                    setProductQuantity(item, newQty < minOrderQty ? 0 : newQty);
                                                                                }
                                                                            }}
                                                                            placeholder="0"
                                                                            className={`w-full h-8 border-y px-1 text-center text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all ${quantity > 0 ? 'border-orange-300 bg-white dark:bg-slate-800 text-orange-600' : 'border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100'}`}
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setProductQuantity(item, quantity === 0 ? minOrderQty : quantity + minOrderQty)}
                                                                            className={`w-7 h-8 flex items-center justify-center rounded-r-lg border border-l-0 text-sm font-bold transition-colors ${quantity > 0 ? 'border-orange-300 bg-orange-50 dark:bg-orange-950/30 text-orange-600 active:bg-orange-100' : 'border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 text-slate-400 active:bg-slate-200'}`}
                                                                        >+</button>
                                                                    </div>
                                                                </div>

                                                                {/* Line Total */}
                                                                <div className="w-28 text-right">
                                                                    {quantity > 0 ? (
                                                                        <div>
                                                                            <p className="text-sm font-bold text-slate-900 dark:text-slate-100">{lineTotal.toFixed(2)} €</p>
                                                                            {hasDiscount && (
                                                                                <p className="text-[10px] text-emerald-600 font-medium">
                                                                                    -{((baseLotPrice - lotPrice) * quantity).toFixed(2)} €
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <p className="text-sm text-slate-300 dark:text-slate-600">—</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {/* Category subtotal */}
                                                {categoryTotal > 0 && (
                                                    <div className="px-5 py-2 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-end gap-2 text-sm">
                                                        <span className="text-slate-500">Sous-total :</span>
                                                        <span className="font-bold text-slate-900 dark:text-slate-100">{categoryTotal.toFixed(2)} €</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Remise supplémentaire section */}
                    <div className="px-5 py-3 bg-amber-50/50 dark:bg-amber-900/10 border-y border-amber-100 dark:border-amber-800/30">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="p-1.5 rounded-lg bg-amber-100 dark:bg-amber-900/30"><Euro className="h-4 w-4 text-amber-600" /></div>
                            <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Remise supplémentaire</span>
                        </div>
                        {/* Type selector */}
                        <div className="flex gap-2 mb-2">
                            <button
                                type="button"
                                onClick={() => setCreditNoteType('amount')}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${creditNoteType === 'amount' ? 'bg-amber-500 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600'}`}
                            >
                                Montant (€)
                            </button>
                            <button
                                type="button"
                                onClick={() => setCreditNoteType('percentage')}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${creditNoteType === 'percentage' ? 'bg-amber-500 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600'}`}
                            >
                                Pourcentage (%)
                            </button>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <div className="flex-1">
                                {creditNoteType === 'amount' ? (
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={creditNoteAmount || ''}
                                        onChange={(e) => setCreditNoteAmount(parseFloat(e.target.value) || 0)}
                                        placeholder="Montant HT (€)"
                                        className="w-full h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                                    />
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            value={creditNotePercentage || ''}
                                            onChange={(e) => setCreditNotePercentage(parseFloat(e.target.value) || 0)}
                                            placeholder="Réduction %"
                                            className="w-full h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                                        />
                                        <span className="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                                            ({(orderTotals.totalHt * (creditNotePercentage / 100)).toFixed(2)} €)
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="flex-[2]">
                                <input
                                    type="text"
                                    value={creditNoteReason}
                                    onChange={(e) => setCreditNoteReason(e.target.value)}
                                    placeholder="Justification (ex: erreur de facturation précédente)"
                                    className="w-full h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Order Summary - Sticky */}
                    <div className="sticky bottom-3 sm:bottom-6 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 dark:from-slate-800 dark:to-slate-900 rounded-2xl shadow-2xl p-3 sm:p-5 text-slate-900 dark:text-white border border-orange-200 dark:border-slate-700 ring-1 ring-orange-200/60 dark:ring-0">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-6">
                            {/* Totals */}
                            <div className="flex-1">
                                <div className="flex items-baseline gap-2 sm:gap-3">
                                    <span className="text-xl sm:text-3xl font-bold">{orderTotals.finalHt.toFixed(2)} €</span>
                                    <span className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">HT</span>
                                </div>
                                {(orderTotals.totalBeforeDiscount > orderTotals.finalHt) && (
                                    <p className="text-sm text-slate-500 dark:text-slate-400 line-through mt-1">
                                        {orderTotals.totalBeforeDiscount.toFixed(2)} € avant remise
                                    </p>
                                )}
                                {orderTotals.creditNoteValue > 0 && (
                                    <div className="mt-1 inline-flex items-center gap-2 bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 px-3 py-1.5 rounded-lg text-sm font-medium">
                                        <Euro className="h-4 w-4" />
                                        {creditNoteType === 'amount' ? `Remise supplémentaire : -${orderTotals.creditNoteValue.toFixed(2)} € HT` : `Remise supplémentaire : -${creditNotePercentage.toFixed(1)}% (${orderTotals.creditNoteValue.toFixed(2)} €)`}
                                    </div>
                                )}
                                <div className="flex items-center gap-2 sm:gap-4 mt-2 sm:mt-3 text-xs sm:text-sm">
                                    <span className="text-slate-600 dark:text-slate-400">TVA {TVA_RATE}% : {orderTotals.taxAmount.toFixed(2)} €</span>
                                    <span className="text-slate-900 dark:text-white font-semibold">TTC : {orderTotals.totalTtc.toFixed(2)} €</span>
                                </div>
                                {orderTotals.totalGain > 0 && (
                                    <div className="mt-3 inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 px-3 py-1.5 rounded-lg text-sm font-medium">
                                        <Check className="h-4 w-4" />
                                        Économie : {orderTotals.totalGain.toFixed(2)} € HT
                                    </div>
                                )}
                            </div>

                            {/* Validation Errors */}
                            {validationErrors.length > 0 && (
                                <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl">
                                    <div className="flex items-start gap-2">
                                        <div className="p-1 rounded-full bg-red-100 dark:bg-red-900/50 mt-0.5">
                                            <svg className="h-3 w-3 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                                                Validation du panachage :
                                            </p>
                                            <ul className="text-xs text-red-700 dark:text-red-300 space-y-1">
                                                {validationErrors.map((error, idx) => (
                                                    <li key={idx}>{error}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                onClick={() => setShowConfirmModal(true)}
                                disabled={!canSubmit() || loading}
                                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-orange-500 hover:bg-orange-400 disabled:bg-amber-200 disabled:text-amber-700 text-white rounded-xl font-semibold text-sm sm:text-base shadow-lg shadow-orange-500/30 hover:shadow-orange-500/40 transition-all disabled:shadow-none disabled:cursor-not-allowed"
                            >
                                {loading ? 'Création...' : 'Créer la commande'}
                            </button>
                        </div>
                    </div>

                    {/* Order Totals - Compact */}
                    <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 rounded-lg p-4 text-slate-900 border border-orange-200">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                            <span className="text-slate-600">Sous-total HT</span>
                            <span className="text-right font-medium">{orderTotals.totalHt.toFixed(2)} €</span>

                            {orderTotals.totalGain > 0 && (
                                <>
                                    <span className="text-emerald-600">Économie</span>
                                    <span className="text-right font-semibold text-emerald-600">-{orderTotals.totalGain.toFixed(2)} €</span>
                                </>
                            )}
                            {orderTotals.creditNoteValue > 0 && (
                                <>
                                    <span className="text-amber-600">Remise supplémentaire</span>
                                    <span className="text-right font-semibold text-amber-600">
                                        -{orderTotals.creditNoteValue.toFixed(2)} €
                                    </span>
                                </>
                            )}

                            <span className="text-slate-600">Net HT (après Remise supplémentaire)</span>
                            <span className="text-right font-bold">{orderTotals.finalHt.toFixed(2)} €</span>

                            <span className="text-slate-600">TVA {TVA_RATE}%</span>
                            <span className="text-right font-medium">{orderTotals.taxAmount.toFixed(2)} €</span>
                        </div>
                        <div className="flex justify-between items-center pt-3 mt-3 border-t border-orange-200">
                            <span className="font-bold">Total TTC</span>
                            <span className="text-xl font-bold">{orderTotals.totalTtc.toFixed(2)} €</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
                        {/* Modal Header */}
                        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Confirmer la commande</h2>
                            <p className="text-sm text-slate-500 mt-1">Vérifiez les détails avant de créer la commande</p>
                        </div>

                        {/* Modal Content */}
                        <div className="p-6 space-y-6">
                            {/* Client Info */}
                            <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Client</h3>
                                <p className="text-base font-medium text-slate-900 dark:text-slate-100">
                                    {selectedClientData?.company_name}
                                </p>
                                {selectedShippingAddress && (() => {
                                    const addr = addresses.find(a => a.id === selectedShippingAddress);
                                    if (!addr) return null;
                                    return (
                                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                                            Livraison : {addr.address}, {addr.postal_code} {addr.city}, {addr.country}
                                        </p>
                                    );
                                })()}
                            </div>

                            {/* Order Items */}
                            <div>
                                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Articles ({cart.length})</h3>
                                <div className="space-y-3 max-h-60 overflow-y-auto">
                                    {cart.map((item) => {
                                        const batchSize = item.consumable.batch_size ?? 1;
                                        const totalUnits = item.quantity * batchSize;
                                        const basePrice = calculateClientPrice(item.consumable);
                                        const baseTotalPrice = item.quantity * basePrice * batchSize;
                                        // Get current tier based on grouped lots
                                        const unitKey = buildUnitVolumeKey(item.consumable.unit_id, item.consumable.batch_size);
                                        const groupedLots = groupedTotalsByUnitId.get(item.consumable.unit_id)?.totalLots
                                            ?? groupedTotalsByUnitKey.get(unitKey)?.totalLots
                                            ?? item.quantity;
                                        const tiers = unitTiersByKey[unitKey] || unitTiersById[item.consumable.unit_id] || [];
                                        const activeTier = detectTier(groupedLots, tiers);
                                        const tierNumber: number | null = activeTier?.tier_number ?? null;
                                        const discountPercent: number = activeTier?.discount_percentage ?? 0;
                                        const hasDiscount = discountPercent > 0;
                                        const discountedTotalPrice = hasDiscount
                                            ? item.quantity * basePrice * (1 - discountPercent / 100) * batchSize
                                            : baseTotalPrice;

                                        return (
                                            <div key={item.consumable.id} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                                <div className="flex items-start justify-between mb-2">
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{item.consumable.title}</p>
                                                        <p className="text-xs text-slate-500">
                                                            {item.quantity} lot{item.quantity > 1 ? 's' : ''} · {totalUnits} {item.consumable.unit}{totalUnits > 1 ? 's' : ''}
                                                        </p>
                                                    </div>
                                                    <div className="text-right">
                                                        {hasDiscount && tierNumber && (
                                                            <span className="inline-block mt-1 px-2 py-0.5 bg-emerald-500 text-white text-[9px] font-bold">P{tierNumber} -{discountPercent}%</span>
                                                        )}
                                                        {hasDiscount && (
                                                            <p className="text-xs text-slate-400 line-through">{baseTotalPrice.toFixed(2)} €</p>
                                                        )}
                                                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                            {discountedTotalPrice.toFixed(2)} €
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Order Totals - Compact */}
                            <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 rounded-lg p-4 text-slate-900 border border-orange-200">
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                    <span className="text-slate-600">Sous-total HT</span>
                                    <span className="text-right font-medium">{orderTotals.totalHt.toFixed(2)} €</span>

                                    {orderTotals.totalGain > 0 && (
                                        <>
                                            <span className="text-emerald-600">Économie</span>
                                            <span className="text-right font-semibold text-emerald-600">-{orderTotals.totalGain.toFixed(2)} €</span>
                                        </>
                                    )}
                                    {orderTotals.creditNoteValue > 0 && (
                                        <>
                                            <span className="text-amber-600">Remise supplémentaire</span>
                                            <span className="text-right font-semibold text-amber-600">
                                                -{orderTotals.creditNoteValue.toFixed(2)} €
                                            </span>
                                        </>
                                    )}

                                    <span className="text-slate-600">Net HT (après Remise supplémentaire)</span>
                                    <span className="text-right font-bold">{orderTotals.finalHt.toFixed(2)} €</span>

                                    <span className="text-slate-600">TVA {TVA_RATE}%</span>
                                    <span className="text-right font-medium">{orderTotals.taxAmount.toFixed(2)} €</span>
                                </div>
                                <div className="flex justify-between items-center pt-3 mt-3 border-t border-orange-200">
                                    <span className="font-bold">Total TTC</span>
                                    <span className="text-xl font-bold">{orderTotals.totalTtc.toFixed(2)} €</span>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex gap-3 justify-end">
                            <button
                                onClick={() => setShowConfirmModal(false)}
                                className="px-6 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={() => {
                                    setShowConfirmModal(false);
                                    handleSubmit();
                                }}
                                disabled={loading}
                                className="px-6 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:bg-amber-200 disabled:text-amber-700 text-white font-semibold shadow-lg shadow-orange-500/30 hover:shadow-orange-500/40 transition-all disabled:shadow-none"
                            >
                                {loading ? 'Création...' : 'Confirmer la commande'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminNewOrder;